import * as React from 'react';
import Panel from '../panel/Panel';
import { observer } from 'mobx-react-lite';
import { useTranslation } from "react-i18next";
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import { IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import HistoryIcon from '@mui/icons-material/History';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import PublisherScheduleDialog from '../publishers/dialogs/PublisherScheduleDialog';
import { useOpenDialog } from '../../hooks/useOpenDialog';
import { Publisher } from '../../domain/Publisher';
import { useStores } from '../../stores';
import SchedulePublisherAssignmentsDialog from './dialogs/SchedulePublisherAssignmentsDialog';

const ScheduleStudentsPage = observer(() => {
    const { t } = useTranslation();
    const { isOpen, openDialog, closeDialog } = useOpenDialog();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { publisherStore } = useStores();
    const [selectedPublisher, setSelectedPublisher] = React.useState<Publisher | undefined>();

    const openPublisherSchedule = (publisher: Publisher) => {
        setSelectedPublisher(publisher);
        openDialog('schedule-history');
    };

    const openAssignmentsDialog = (publisher: Publisher) => {
        setSelectedPublisher(publisher);
        openDialog('publisher-assignments');
    };

    const students = React.useMemo(() => (
        publisherStore.publishers
            ?.filter(publisher => publisher.assignments.some(assignment => assignment.id === 8)) || []
    ), [publisherStore.publishers]);

    const handleCloseDialog = () => {
        setSelectedPublisher(undefined);
        closeDialog();
    };

    return (
        <Panel
            title={t('students')}
            headerItems={[]}>
            <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h5">{t('students')}</Typography>

                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>#</TableCell>
                                    <TableCell>{t('name')}</TableCell>
                                    {!isSmallScreen && <TableCell>{t('assignment')}</TableCell>}
                                    <TableCell align="center">{t('actions')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {students.map((publisher, index) => (
                                    <TableRow key={publisher.id}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>{`${publisher.lastName} ${publisher.firstName}`}</TableCell>
                                        {!isSmallScreen && (
                                            <TableCell>{publisher.appointments.map(appointment => t(appointment.name))[0]}</TableCell>
                                        )}
                                        <TableCell align="center">
                                            <Tooltip title={t('history')}>
                                                <IconButton
                                                    aria-label={t('history')}
                                                    onClick={() => openPublisherSchedule(publisher)}
                                                    size="small"
                                                >
                                                    <HistoryIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={t('assignments')}>
                                                <IconButton
                                                    aria-label={t('assignments')}
                                                    onClick={() => openAssignmentsDialog(publisher)}
                                                    size="small"
                                                    sx={{ ml: 1 }}
                                                >
                                                    <AssignmentIndIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Container>
            {selectedPublisher && (
                <PublisherScheduleDialog
                    isOpen={isOpen('schedule-history')}
                    publisherId={selectedPublisher.id}
                    onClose={handleCloseDialog}
                />
            )}
            {selectedPublisher && (
                <SchedulePublisherAssignmentsDialog
                    isOpen={isOpen('publisher-assignments')}
                    publisher={selectedPublisher}
                    onClose={handleCloseDialog}
                />
            )}
        </Panel>
    );
});

export default ScheduleStudentsPage;